import { InitialsAvatar } from '@/client/components/common/InitialsAvatar';
import { supabase } from '@/client/lib/supabase';
import { cn } from '@/client/lib/utils';
import { trpc } from '@/client/utils/trpc';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { ChevronsLeft, ChevronsRight, LogOut, X } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { type NavItem, SETTINGS_ITEM, getNavSections } from './nav';

const COLLAPSE_KEY = 'sidebar-collapsed';

export function GaraMark({ size = 28 }: { size?: number }) {
  // Unique per instance: a hidden copy (e.g. the desktop rail on mobile) must not own the gradient.
  const gradientId = `gara-mark-${useId().replace(/:/g, '')}`;
  return (
    <svg width={size} height={size} viewBox="0 0 28 28" fill="none" aria-hidden>
      <title>Gara</title>
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="28" y2="28" gradientUnits="userSpaceOnUse">
          <stop stopColor="hsl(var(--primary))" />
          <stop offset="1" stopColor="hsl(var(--brand-2))" />
        </linearGradient>
      </defs>
      <rect width="28" height="28" rx="8" fill={`url(#${gradientId})`} />
      <rect x="0.5" y="0.5" width="27" height="27" rx="7.5" stroke="white" strokeOpacity="0.18" />
      <path
        d="M19.5 11.2c-.7-1.7-2.4-2.9-4.5-2.9-2.8 0-5 2.2-5 5s2.2 5 5 5c2.4 0 4.4-1.7 4.9-4h-4.5"
        stroke="white"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function NavLink({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      activeOptions={item.to === '/' ? { exact: true } : undefined}
      title={collapsed ? item.label : undefined}
      className={cn(
        'group relative flex h-9 items-center gap-3 rounded-lg px-2.5 text-[13.5px] font-medium text-sidebar-foreground transition-colors',
        'hover:bg-muted hover:text-foreground',
        '[&.active]:bg-sidebar-accent [&.active]:text-sidebar-accent-foreground',
        collapsed && 'justify-center px-0',
      )}
    >
      <span
        aria-hidden
        className="absolute -left-3 top-1/2 h-5 w-[3px] -translate-y-1/2 scale-y-0 rounded-r-full bg-primary transition-transform duration-200 group-[.active]:scale-y-100"
      />
      <Icon className="h-[17px] w-[17px] shrink-0 opacity-70 transition-opacity group-hover:opacity-100 group-[.active]:text-primary group-[.active]:opacity-100" />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </Link>
  );
}

function useSidebarData() {
  const { data: me } = trpc.user.me.useQuery();
  const navigate = useNavigate();
  async function handleLogout() {
    await supabase.auth.signOut();
    navigate({ to: '/login' });
  }
  return { me, sections: getNavSections(me?.role === 'ADMIN'), handleLogout };
}

/** Brand, nav and profile footer — shared by the desktop rail and the mobile drawer. */
function SidebarBody({ collapsed }: { collapsed: boolean }) {
  const { me, sections, handleLogout } = useSidebarData();
  return (
    <>
      {/* Brand */}
      <div
        className={cn(
          'flex h-14 shrink-0 items-center gap-2.5 px-4',
          collapsed && 'justify-center px-0',
        )}
      >
        <GaraMark />
        {!collapsed && (
          <span className="text-[15px] font-semibold tracking-tight">
            Gara<span className="font-medium text-muted-foreground">Tech</span>
          </span>
        )}
      </div>

      {/* Nav */}
      <nav className="min-h-0 flex-1 space-y-5 overflow-y-auto px-3 pb-3 pt-2">
        {sections.map((section) => (
          <div key={section.label} className="space-y-0.5">
            {collapsed ? (
              <div className="mx-auto mb-2 h-px w-6 bg-sidebar-border first:hidden" />
            ) : (
              <p className="px-2.5 pb-1.5 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground/70">
                {section.label}
              </p>
            )}
            {section.items.map((item) => (
              <NavLink key={item.to} item={item} collapsed={collapsed} />
            ))}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="shrink-0 space-y-1 border-t border-sidebar-border p-3">
        <NavLink item={SETTINGS_ITEM} collapsed={collapsed} />
        <div
          className={cn(
            'mt-2 flex items-center gap-2.5 rounded-xl border border-sidebar-border bg-muted/40 p-2',
            collapsed && 'flex-col gap-2 border-transparent bg-transparent p-0',
          )}
        >
          <Link
            to="/settings"
            title={collapsed ? (me?.full_name ?? 'Profile') : undefined}
            className="flex min-w-0 flex-1 items-center gap-2.5 rounded-lg"
          >
            <InitialsAvatar name={me?.full_name ?? 'User'} size={32} />
            {!collapsed && (
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-semibold leading-tight">
                  {me?.full_name ?? 'Loading…'}
                </div>
                <div className="mt-0.5 truncate text-[11.5px] capitalize leading-tight text-muted-foreground">
                  {me?.role?.toLowerCase() ?? ''}
                </div>
              </div>
            )}
          </Link>
          <button
            type="button"
            onClick={handleLogout}
            title="Log out"
            aria-label="Log out"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </>
  );
}

/** Desktop rail (lg and up). Collapsible to icons; the choice is remembered. */
export function Sidebar() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      // ignore
    }
  }, [collapsed]);

  return (
    <aside
      className={cn(
        'relative hidden h-[100dvh] shrink-0 flex-col border-r border-sidebar-border bg-sidebar transition-[width] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] lg:flex',
        collapsed ? 'w-[68px]' : 'w-[248px]',
      )}
    >
      <SidebarBody collapsed={collapsed} />
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        className="absolute -right-3 top-[18px] z-10 flex h-6 w-6 items-center justify-center rounded-full border bg-card text-muted-foreground shadow-soft transition-colors hover:text-foreground"
      >
        {collapsed ? (
          <ChevronsRight className="h-3.5 w-3.5" />
        ) : (
          <ChevronsLeft className="h-3.5 w-3.5" />
        )}
      </button>
    </aside>
  );
}

/** Slide-in drawer for phones and tablets (below lg). Closes itself on navigation. */
export function MobileSidebar({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // biome-ignore lint/correctness/useExhaustiveDependencies: close whenever the route changes
  useEffect(() => {
    onOpenChange(false);
  }, [pathname]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px] data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0 lg:hidden" />
        <DialogPrimitive.Content className="fixed inset-y-0 left-0 z-50 flex h-[100dvh] w-[280px] max-w-[85vw] flex-col border-r border-sidebar-border bg-sidebar shadow-overlay duration-300 data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left data-[state=open]:animate-in data-[state=open]:slide-in-from-left lg:hidden">
          <DialogPrimitive.Title className="sr-only">Navigation</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">Main menu</DialogPrimitive.Description>
          <SidebarBody collapsed={false} />
          <DialogPrimitive.Close
            aria-label="Close menu"
            className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
